import React, {Fragment} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import './UnitUpgradeModal.css';
import '../../TokensToolbar/NewUnitToolbar/SelectedUnitStats/SelectedUnitStats.css';
import {getAvailableUnitUpgrades} from '../../../utils/unit/getAvailableUnitUpgrades';
import {getUnitTypeConfig} from '../../../configs/units';
import {hideUnitUpgradeModal, showToast, upgradeUnit} from '../../../redux/actions';
import {getUnitIconClassNameForButton} from '../../../utils/unit/getUnitIconClassNameForButton';
import {
  selectCurrentPlayer,
  selectShowUnitUpgradeModal,
  selectUnitForUpgradeId,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import ModalWindow from "../ModalWindow/ModalWindow";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {playUnitUpgradeEffects} from "../../../scenes/playAbilityCastEffects";
import ModalButton from "../ModalWindow/ModalButton/ModalButton";
import UnitIcon from "../../TokensToolbar/NewUnitToolbar/UnitIcon/UnitIcon";
import {checkIfUnitTypeChangeExceedsLimit} from "../../../utils/gameState/recruitment/checkIfUnitTypeChangeExceedsLimit";
import {getSameTypeUnitLimit} from "../../../utils/gameState/recruitment/checkSameTypeUnitRecruitmentLimit";
import {makeTranslucent} from "../../../utils/style/classNames";

const UnitUpgradeModal = () => {
  const dispatch = useDispatch();
  const store = useStore();

  const showModal = useSelector(selectShowUnitUpgradeModal);
  const unitForUpgradeId = useSelector(selectUnitForUpgradeId);
  const units = useSelector(selectUnits);
  const currentPlayer = useSelector(selectCurrentPlayer);

  if (!showModal) return null;

  const unit = units.find(u => u.id === unitForUpgradeId);

  // Только типы, доступные фракции игрока (unitTypes в конфиге фракции) —
  // например, у гномов кавалерия не предлагается вовсе
  const availableUpgrades = getAvailableUnitUpgrades(unit.type, unit.factionId);

  // Лимит отрядов одного типа держится и при улучшении: вариант может вести
  // в «младший» тип (d10 -> d8), которого у игрока уже по лимиту. Такие
  // варианты видны, но приглушены, а клик объясняет причину тем же тостом, что и найм
  const checkIfUpgradeExceedsLimit = (upgradeOption) =>
    checkIfUnitTypeChangeExceedsLimit(unit.id, upgradeOption.toId, currentPlayer, units);

  const handleUpgradeSelect = (upgradeOption) => {
    if (checkIfUpgradeExceedsLimit(upgradeOption)) {
      const limit = getSameTypeUnitLimit(currentPlayer, upgradeOption.toId);
      dispatch(showToast(`Нельзя иметь более ${limit} отрядов этого типа!`, 'warning', 5000));
      return;
    }

    if (currentPlayer.coins < upgradeOption.price) {
      dispatch(showToast('Недостаточно монет для улучшения отряда!', 'warning', 3000));
      return;
    }

    const prevState = store.getState();
    dispatch(upgradeUnit(unit.id, unit.type, upgradeOption.toId, upgradeOption.price));
    dispatch(hideUnitUpgradeModal());
    // Шевроны, звук и монеты; экшен могли отвергнуть — тогда показывать нечего
    const upgradedUnit = selectUnits(store.getState()).find((item) => item.id === unit.id);
    if (upgradedUnit?.type === upgradeOption.toId) {
      playUnitUpgradeEffects(store, REGISTRY_SINGLETON.getHexGridRenderer(), REGISTRY_SINGLETON.getSoundManager(), {
        unitId: unit.id,
        newType: upgradeOption.toId,
        price: upgradeOption.price,
        prevState,
      });
    }
  };

  const handleClose = () => {
    dispatch(hideUnitUpgradeModal());
  };

  return (
    <ModalWindow handleClose={handleClose}>
      <div className="unit-upgrade-modal_header">
        <h2>Улучшение отряда</h2>
        <UnitIcon
          unitType={unit.type}
          playerColor={currentPlayer.color}
          factionId={currentPlayer.factionId}
          hasUnitSkin={currentPlayer.hasUnitSkin}
          className={`unit-upgrade-modal_unit-icon ${getUnitIconClassNameForButton(unit.type)}`}
        />
      </div>

      <div className="unit-upgrade-modal_options">
        <div className={'new-units-toolbar_stat-cell'}/>
        <img
          className={'new-units-toolbar_stat-cell_stat-img'}
          src={'/img/icons/unit size.png'}
          alt={'unit size'}
        />
        <img
          className={'new-units-toolbar_stat-cell_stat-img'}
          src={'/img/icons/speed.png'}
          alt="Дальность перемещения"
          title="Дальность перемещения"
        />
        <img
          className={'new-units-toolbar_stat-cell_stat-img'}
          src={'/img/icons/coins.png'}
          alt={'speed'}
        />
        <div className={'new-units-toolbar_stat-cell'}/>

        {availableUpgrades.map((upgrade) => {
          const targetUnitConfig = getUnitTypeConfig(upgrade.toId);
          const isLimitExceeded = checkIfUpgradeExceedsLimit(upgrade);
          const translucentClassName = makeTranslucent(isLimitExceeded);

          return (
            <Fragment
              key={upgrade.toId}
            >
              <UnitIcon
                unitType={upgrade.toId}
                playerColor={currentPlayer.color}
                factionId={currentPlayer.factionId}
                hasUnitSkin={currentPlayer.hasUnitSkin}
                className={`unit-upgrade-modal_option-icon ${getUnitIconClassNameForButton(upgrade.toId)} ${translucentClassName}`}
              />

              <div className={`new-units-toolbar_stat-cell_text text-black ${translucentClassName}`}>
                {targetUnitConfig.maxSize}
              </div>


              <div className={`new-units-toolbar_stat-cell_text text-controls ${translucentClassName}`}>
                {targetUnitConfig.speed}
              </div>

              <div className={`new-units-toolbar_stat-cell_text text-coins-dark ${translucentClassName}`}>
                {upgrade.price}
              </div>

              <button
                className={`unit-upgrade-modal_option-button ${translucentClassName}`}
                title={isLimitExceeded ? 'Отрядов этого типа уже максимум' : undefined}
                onClick={() => handleUpgradeSelect(upgrade)}
              >
                <img
                  src={'/img/icons/upgrade.png'}
                  alt={'Upgrade'}
                />
              </button>
            </Fragment>
          );
        })}
      </div>

      <div className="city-capture-modal-footer">
        <ModalButton
          onClick={handleClose}
          text={'Продолжить'}
        />
      </div>
    </ModalWindow>
  );
};

export default UnitUpgradeModal;
